<script setup>
defineOptions({ name: "ContratoModulePage" });
import { defineAsyncComponent } from "vue";
import {
  FolderIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  ReceiptPercentIcon,
  ComputerDesktopIcon,
  CalculatorIcon,
  WrenchScrewdriverIcon,
  AcademicCapIcon,
  UserGroupIcon,
  MegaphoneIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-contrato.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "Sua receita recorrente começa no contrato | Kali Sistemas",
  description:
    "Centralize contratos, mensalidades, vigências e reajustes no Kali Gestão e conecte seus acordos comerciais ao financeiro da empresa.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional Contrato, descritas no material do produto.
const capacidades = [
  {
    icon: FolderIcon,
    titulo: "Carteira organizada",
    texto: "Tenha uma visão dos contratos ativos e das condições comerciais de cada cliente.",
  },
  {
    icon: ArrowPathIcon,
    titulo: "Receita recorrente",
    texto: "Relacione contratos a mensalidades e serviços contínuos.",
  },
  {
    icon: CalendarDaysIcon,
    titulo: "Vigência",
    texto: "Acompanhe início, término e continuidade dos contratos.",
  },
  {
    icon: ReceiptPercentIcon,
    titulo: "Reajustes",
    texto: "Organize alterações de valores ao longo da relação comercial.",
  },
];

const steps = [
  {
    title: "Contrato é criado",
    description: "Cliente, serviços, valores e condições são definidos.",
  },
  {
    title: "Vigência é estabelecida",
    description: "O período contratual e as regras comerciais ficam registrados.",
  },
  {
    title: "Recorrência é configurada",
    description: "Mensal, trimestral, anual ou conforme o modelo da operação.",
  },
  {
    title: "Faturamento acontece",
    description: "As cobranças relacionadas ao contrato são geradas conforme o fluxo disponível.",
  },
  {
    title: "Financeiro é atualizado",
    description: "Os valores passam a fazer parte das contas a receber.",
  },
];

// Perfis de negócio que vivem de relacionamento contínuo e recorrência.
const segmentos = [
  {
    icon: ComputerDesktopIcon,
    titulo: "Tecnologia e software",
    texto: "Licenciamento, suporte, hospedagem e serviços recorrentes.",
  },
  {
    icon: CalculatorIcon,
    titulo: "Contabilidade e consultoria",
    texto: "Mensalidades e contratos de prestação continuada.",
  },
  {
    icon: WrenchScrewdriverIcon,
    titulo: "Manutenção",
    texto: "Planos preventivos e contratos periódicos.",
  },
  {
    icon: AcademicCapIcon,
    titulo: "Educação",
    texto: "Mensalidades, cursos e planos recorrentes.",
  },
  {
    icon: UserGroupIcon,
    titulo: "Academias e associações",
    texto: "Planos mensais, trimestrais ou anuais.",
  },
  {
    icon: MegaphoneIcon,
    titulo: "Marketing e agências",
    texto: "Contratos de gestão, serviços e acompanhamento mensal.",
  },
];

const faqItems = [
  {
    question: "Posso trabalhar com mensalidades?",
    answer:
      "Sim. O recurso é especialmente útil para empresas que realizam cobranças recorrentes.",
  },
  {
    question: "Posso definir uma vigência?",
    answer:
      "Sim. O contrato pode conter informações de início e término ou ser utilizado em relações contínuas, conforme o fluxo disponível.",
  },
  {
    question: "Consigo organizar reajustes?",
    answer:
      "As informações de reajuste podem fazer parte da gestão contratual.",
  },
  {
    question: "O contrato se conecta ao financeiro?",
    answer:
      "Sim. O faturamento relacionado ao contrato pode integrar o fluxo de contas a receber.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="Sua receita recorrente começa no contrato"
      description="Centralize contratos, mensalidades, vigências e reajustes no Kali Gestão e conecte seus acordos comerciais ao financeiro da empresa. Menos controles paralelos para sua equipe, mais previsibilidade para sua operação."
      primary-label="Solicitar ativação" :primary-href="portalUrl" :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão" secondary-href="/solucao/kali-gestao" :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Não é apenas guardar um contrato. É gerenciar o que acontece depois dele.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Depois que um contrato é fechado, começam várias
            responsabilidades: acompanhar vigência, gerar cobranças,
            controlar mensalidades, aplicar reajustes e manter o histórico
            da relação comercial. O módulo de Contratos centraliza essas
            informações e transforma acordos recorrentes em processos
            organizados.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="cap in capacidades" :key="cap.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Cadastre uma vez. Acompanhe toda a recorrência.
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Do contrato ao financeiro, sem controle paralelo.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div v-for="(step, idx) in steps" :key="step.title" v-reveal class="p-7 bg-white rounded-3xl shadow-sm">
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600">
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Para quem é -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Ideal para empresas que vivem de relacionamento contínuo
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="s in segmentos" :key="s.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel title="Dúvidas sobre Contrato" :items="faqItems" cta-label="Solicitar ativação" :cta-href="portalUrl"
      description="Respostas rápidas sobre o módulo Contrato. Se preferir, fale com nosso time e te orientamos no melhor cenário." />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
